import { ServiceLayout } from "@/components/ServiceLayout"
import { predictiveSoftwareFAQs } from "@/data/faqData"

const PredictiveSoftware = () => {
  return (
    <ServiceLayout
      title="Predictive Software Ecosystems"
      faqs={predictiveSoftwareFAQs}
      faqTitle="Predictive Software FAQs"
      faqSubtitle="Common questions about self-optimizing and AI-driven software"
      description="Developing bespoke software architectures that integrate machine learning pipelines to optimize workflows and decision-making automatically."
      heroImage="https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?auto=format&fit=crop&q=80&w=2600"
      features={[
        {
          title: "Self-Optimizing Algorithms",
          description:
            "Software that monitors its own performance and adjusts parameters to maximize throughput and minimize resource consumption.",
        },
        {
          title: "Automated Decision Support",
          description:
            "Embed decision engines that provide confidence-scored recommendations to human operators in real-time.",
        },
        {
          title: "Event-Driven Architecture",
          description:
            "Reactive systems built on Kafka/RabbitMQ that process millions of events to trigger immediate business actions.",
        },
      ]}
      approach={{
        title: "Stochastic Software Design",
        description:
          "Software that learns from every transaction to functionally improve its own efficiency.",
        image:
          "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&q=80&w=2600",
        content: [
          "Traditional software is deterministic: Input A always leads to Output B. We engineer 'Stochastic Software' that can handle ambiguity and optimize itself. Our derivation process involves 'Algorithmic Auditing' of your current business logic to identify heuristic rules that can be replaced by probabilistic machine learning models.",
          "We construct an 'Event-Driven Neural Nervous System' using Apache Kafka or NATS, where every interaction is a signal. These signals feed into real-time reinforcement learning agents that tweak system parameters - such as identifying the optimal time to send a notification or dynamically routing a support ticket to the best-suited agent.",
          "This culminates in a 'Shadow Mode' deployment strategy. We run the new predictive models in parallel with existing logic, validating their accuracy against historical data before promoting them to active decision-making. This guarantees that the system only takes over when it is provably superior to the status quo.",
        ],
      }}
      benefits={[
        "Automated Error Recovery",
        "Data-Driven Workflow Routing",
        "Seamless API Integrations",
        "Legacy System Modernization",
        "Role-Based Access Control (RBAC)",
        "Audit-Ready Logging",
      ]}
      process={{
        steps: [
          {
            title: "Architecture Assessment",
            description:
              "Reviewing existing systems and data flows to identify bottlenecks and opportunities for predictive automation.",
          },
          {
            title: "Data Pipeline Construction",
            description:
              "Building robust ETL pipelines to ingest, clean, and normalize data for machine learning models.",
          },
          {
            title: "Model Development",
            description:
              "Training and validating custom ML models tailored to your specific business logic and prediction goals.",
          },
          {
            title: "Integration & Deployment",
            description:
              "Embedding models into the software application via APIs and setting up monitoring for drift and accuracy.",
          },
        ],
      }}
      techStack={[
        {
          category: "Languages",
          items: ["Python", "Go", "Java", "TypeScript"],
        },
        {
          category: "Data & ML",
          items: ["TensorFlow", "Pandas", "Apache Spark", "Airflow"],
        },
        {
          category: "Architecture",
          items: ["Docker", "Kubernetes", "Kafka", "PostgreSQL"],
        },
      ]}
    />
  )
}

export default PredictiveSoftware
